import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Loader2, Download, Package } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from "recharts";
import api from "../../lib/api";

const COLORS = ["hsl(var(--primary))", "#6366f1", "#0ea5e9", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316", "#84cc16", "#a855f7"];

function downloadCSV(data, filename) {
  const csv = [Object.keys(data[0]).join(","), ...data.map(r => Object.values(r).join(","))].join("\n");
  const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(new Blob([csv], { type: "text/csv" })), download: filename });
  a.click(); URL.revokeObjectURL(a.href);
}

export function ProductsTab({ restaurantId, startDate, endDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("revenue"); // "revenue" | "qty"

  useEffect(() => {
    if (!restaurantId) return;
    setLoading(true);
    api.request(`/restaurants/${restaurantId}/analytics/products?${new URLSearchParams({ ...(startDate && { start_date: startDate }), ...(endDate && { end_date: endDate }) })}`)
      .then(setData).catch(console.error).finally(() => setLoading(false));
  }, [restaurantId, startDate, endDate]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  if (!data) return null;

  const { top_by_revenue, top_by_qty, categories, total_products } = data;
  const displayList = view === "revenue" ? top_by_revenue.slice(0, 10) : top_by_qty.slice(0, 10);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Badge variant="outline" className="text-xs">{total_products} productos distintos</Badge>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-border overflow-hidden text-xs">
            <button onClick={() => setView("revenue")} className={`px-3 py-1.5 transition-colors ${view === "revenue" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}>Por ingresos</button>
            <button onClick={() => setView("qty")} className={`px-3 py-1.5 transition-colors ${view === "qty" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}>Por cantidad</button>
          </div>
          <Button variant="outline" size="sm" onClick={() => downloadCSV(top_by_revenue, "productos.csv")} className="gap-1 text-xs" data-testid="export-products-csv">
            <Download className="w-3 h-3" /> CSV
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top products table */}
        <Card data-testid="products-table">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Package className="w-4 h-4" />Top 10 productos</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {displayList.map((p, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-2.5">
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${i === 0 ? "bg-amber-100 text-amber-700" : i === 1 ? "bg-muted text-muted-foreground" : i === 2 ? "bg-orange-100 text-orange-700" : "bg-muted text-muted-foreground"}`}>{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{p.product}</p>
                    {view === "revenue" && p.avg_price > 0 && <p className="text-xs text-muted-foreground">{p.qty} uds · {p.avg_price}€/u</p>}
                    {view === "qty" && <p className="text-xs text-muted-foreground">{p.revenue}€ facturado</p>}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold">{view === "revenue" ? `${p.revenue}€` : `×${p.qty}`}</p>
                    {view === "revenue" && <p className="text-xs text-muted-foreground">{p.pct}%</p>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Category pie */}
        <Card data-testid="products-pie">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Distribución por categoría</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={categories} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={40} paddingAngle={2}>
                  {categories.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={v => [`${v}€`, "Ventas"]} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Revenue bar chart */}
      <Card data-testid="products-bar-chart">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Top 10 por ingresos</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={top_by_revenue.slice(0, 10)} layout="vertical" barSize={14}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={v => `${v}€`} />
              <YAxis type="category" dataKey="product" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} width={120} />
              <Tooltip formatter={v => [`${v}€`, "Ingresos"]} />
              <Bar dataKey="revenue" fill="hsl(var(--primary))" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}
